import { Tooltip } from '@mantine/core'

import { Frequency, TriggerDelayType } from 'src/graphql/types/automatedRules'
import IconClock from 'src/icons/IconClock'
import IconRefresh from 'src/icons/IconRefresh'

const ICON_CLASS = 'h-2.5 w-2.5 fill-none stroke-current'

const DELAY_UNIT_LABEL: Record<string, { short: string; long: string }> = {
  [TriggerDelayType.MINUTE]: { short: 'm', long: 'minute' },
  [TriggerDelayType.HOUR]: { short: 'h', long: 'hour' },
  [TriggerDelayType.DAY]: { short: 'd', long: 'day' },
  [TriggerDelayType.WEEK]: { short: 'w', long: 'week' },
  [TriggerDelayType.MONTH]: { short: 'mo', long: 'month' },
}

const FREQUENCY_INTERVAL: Record<string, { short: string; long: string }> = {
  [Frequency.MINUTE]: { short: 'm', long: 'minute' },
  [Frequency.HOURLY]: { short: 'h', long: 'hour' },
  [Frequency.DAILY]: { short: 'd', long: 'day' },
  [Frequency.WEEKLY]: { short: 'w', long: 'week' },
  [Frequency.MONTHLY]: { short: 'mo', long: 'month' },
  [Frequency.QUARTERLY]: { short: 'q', long: 'quarter' },
  [Frequency.BIANNUALLY]: { short: '6mo', long: 'half-year' },
  [Frequency.YEARLY]: { short: 'y', long: 'year' },
}

const pluralize = (n: number, label: string): string =>
  n === 1 ? label : `${label}s`

type DelayPillProps = {
  triggerDelay: number | null | undefined
  triggerDelayType: string | null | undefined
}

export const DelayPill = ({ triggerDelay, triggerDelayType }: DelayPillProps) => {
  if (triggerDelay == null || triggerDelay <= 0 || !triggerDelayType) return null
  const unit = DELAY_UNIT_LABEL[triggerDelayType]
  if (!unit) return null

  const shortLabel = `+${triggerDelay}${unit.short}`
  const tooltip = `Fires ${triggerDelay} ${pluralize(
    triggerDelay,
    unit.long
  )} after the trigger`

  return (
    <Tooltip label={tooltip} withArrow withinPortal position="top">
      <span className="inline-flex cursor-default items-center gap-1 rounded-full border border-doubleNickel-gray-300 bg-white px-2 py-0.5 text-[10px] font-medium text-doubleNickel-gray-900 transition-colors hover:border-doubleNickel-gray-500">
        <IconClock className={`${ICON_CLASS} text-doubleNickel-gray-600`} />
        {shortLabel}
      </span>
    </Tooltip>
  )
}

type CadencePillProps = {
  frequency: string | null | undefined
  // The interval multiplier (e.g., frequency=DAILY + frequencyInterval=3
  // means "every 3 days"). Defaults to 1 when null.
  frequencyInterval: number | null | undefined
  // Total number of times the rule may fire. Drives the "×N" cap badge.
  // Null = no cap (rule fires until disabled).
  executionLimit: number | null | undefined
}

export const CadencePill = ({
  frequency,
  frequencyInterval,
  executionLimit,
}: CadencePillProps) => {
  if (!frequency || frequency === Frequency.ONCE) return null
  const unit = FREQUENCY_INTERVAL[frequency]
  if (!unit) return null

  const interval =
    frequencyInterval && frequencyInterval > 0 ? frequencyInterval : 1
  const cap = executionLimit && executionLimit > 0 ? executionLimit : null

  const tooltip = cap
    ? `Repeats every ${interval} ${pluralize(interval, unit.long)}, up to ${cap} times`
    : `Repeats every ${interval} ${pluralize(interval, unit.long)}`

  return (
    <Tooltip label={tooltip} withArrow withinPortal position="top">
      <span className="inline-flex cursor-default items-center gap-1 rounded-full border border-doubleNickel-gray-300 bg-white px-2 py-0.5 text-[10px] font-medium text-doubleNickel-gray-900 transition-colors hover:border-doubleNickel-gray-500">
        <IconRefresh className={`${ICON_CLASS} text-doubleNickel-gray-600`} />
        {`${interval}${unit.short}`}
        {cap ? (
          <span className="rounded-full bg-doubleNickel-gray-700 px-1 text-[9px] font-bold text-white">
            ×{cap}
          </span>
        ) : null}
      </span>
    </Tooltip>
  )
}

type ScheduleFooterProps = {
  triggerDelay: number | null | undefined
  triggerDelayType: string | null | undefined
  frequency: string | null | undefined
  frequencyInterval: number | null | undefined
  executionLimit: number | null | undefined
}

const ScheduleFooter = (props: ScheduleFooterProps) => {
  const showDelay =
    props.triggerDelay != null && props.triggerDelay > 0 && props.triggerDelayType
  const showCadence = props.frequency && props.frequency !== Frequency.ONCE

  if (!showDelay && !showCadence) return null

  return (
    <div className="flex items-center gap-1.5">
      <DelayPill
        triggerDelay={props.triggerDelay}
        triggerDelayType={props.triggerDelayType}
      />
      <CadencePill
        frequency={props.frequency}
        frequencyInterval={props.frequencyInterval}
        executionLimit={props.executionLimit}
      />
    </div>
  )
}

export default ScheduleFooter
